import { KeyValue } from '@shared/interfaces/key-value/key-value';

/**
 * Configuration options for transforming an enum into a key-value array.
 */
interface EnumConfig<T extends object> {
  /** Prefix to add to each key in the result. */
  prefixKey?: string;
  /** Prefix to add to each value in the result. */
  prefixValue?: string;
  /** If true, converts both keys and values to lowercase. */
  lowercase?: boolean;
  /** If true, trims whitespace from both keys and values. */
  trim?: boolean;
  /** List of enum keys to omit from the result. */
  omitKeys?: Array<keyof T>;
  /** List of enum keys to include in the result (cannot be used with omitKeys). */
  includeKeys?: Array<keyof T>;
}

/**
 * Default configuration used when none is provided.
 */
const defaultEnumConfig = {
  lowercase: true,
  trim: true,
} satisfies EnumConfig<any>;

/**
 * Transforms a TypeScript enum or object into an array of key-value pairs,
 * optionally applying formatting and filtering based on configuration.
 *
 * @template T - The enum or object type.
 * @param enm - The enum or object to convert.
 * @param config - Optional configuration for formatting and filtering the output.
 * @returns An array of key-value pairs suitable for use in dropdowns or lookups.
 *
 * @throws If both `omitKeys` and `includeKeys` are specified in the configuration.
 */
export function enumToKeyValue<T extends object>(
  enm: T,
  config: EnumConfig<T> = defaultEnumConfig
): KeyValue[] {
  const enumConfig: EnumConfig<T> = {
    ...defaultEnumConfig,
    ...config,
  };

  if (enumConfig.omitKeys && enumConfig.includeKeys) {
    throw new Error("You can only specify either 'omitKeys' or 'includeKeys', not both.");
  }

  let entries = Object.entries(enm) as [keyof T, any][];

  if (enumConfig.includeKeys) {
    entries = entries.filter(([key]) => enumConfig.includeKeys!.includes(key));
  } else if (enumConfig.omitKeys) {
    entries = entries.filter(([key]) => !enumConfig.omitKeys!.includes(key));
  }

  return entries.map(([key, value]) => {
    let processedKey = String(key);
    let processedValue = String(value);

    if (enumConfig.lowercase) {
      processedValue = processedValue.toLowerCase();
    }

    if (enumConfig.trim) {
      processedKey = processedKey.trim();
      processedValue = processedValue.trim();
    }

    if (enumConfig.prefixKey) {
      processedKey = `${enumConfig.prefixKey}${processedKey}`;
    }

    if (enumConfig.prefixValue) {
      processedValue = `${enumConfig.prefixValue}${processedValue}`;
    }

    return { key: processedKey, value: processedValue };
  });
}
